/**
 * propertyEditTabs.js — Configuration des onglets de la fiche d'édition v5
 * 
 * Référence : /app/frontend/public/property_edit_preview_v5.html
 * Convention : /app/memory/DESIGN_CONVENTIONS.md
 */

import { getByPath } from './propertyFieldRules';
import exploitationLayout from './exploitationLayout.json';

/**
 * iter105 — L'onglet Exploitation est visible dès qu'au moins un bloc de
 * exploitationLayout.json a sa condition satisfaite (mécanisme générique, aucun
 * hardcode d'objet). Un profil supplémentaire = un simple ajout dans le JSON.
 */
function hasApplicableExploitationBlock(taxonomy) {
  const t = taxonomy || {};
  return (exploitationLayout.sections || []).some(section => {
    const cond = section.condition || {};
    if (cond.operation && !cond.operation.includes(t.operation)) return false;
    if (cond.objet && !cond.objet.includes(t.objet)) return false;
    if (cond.profil && !cond.profil.includes(t.profil)) return false;
    if (cond.sous_profil && !cond.sous_profil.includes(t.sous_profil)) return false;
    return true;
  });
}

/**
 * Liste ordonnée des onglets avec leurs métadonnées.
 * `opOnly` : si renseigné, l'onglet n'apparaît que pour ces opérations.
 */
export const TABS = [
  { id: 'general',      label: 'Général',               icon: 'bi-info-circle',       iconColor: 'text-blue-600',     bg: 'bg-blue-100',     helper: "Type d'opération, titre, localisation" },
  { id: 'composition',  label: 'Détails',               icon: 'bi-grid-3x3-gap',      iconColor: 'text-emerald-600',  bg: 'bg-emerald-100',  helper: 'Local, exploitation, chiffres — tout ce qui décrit le bien', excludeOp: ['licence'] },
  { id: 'descriptif',   label: 'Descriptif',            icon: 'bi-textarea-t',        iconColor: 'text-slate-600',   bg: 'bg-slate-100',   helper: 'Description courte + détaillée' },
  { id: 'financieres',  label: 'Finances',              icon: 'bi-currency-euro',     iconColor: 'text-emerald-600',  bg: 'bg-emerald-100',  helper: 'Prix, loyer, charges et performance' },
  { id: 'diagnostics',  label: 'Diagnostics',           icon: 'bi-lightning-charge',  iconColor: 'text-emerald-600',  bg: 'bg-emerald-100',  helper: 'DPE et GES', excludeObjet: ['licence_exploitation', 'licence', 'parts_sociales'] },
  { id: 'mandat',       label: 'Mandat',                icon: 'bi-file-earmark-text', iconColor: 'text-blue-600',     bg: 'bg-blue-100',     helper: 'Numéro, type, dates, transférabilité' },
  { id: 'medias',       label: 'Médias',                icon: 'bi-images',            iconColor: 'text-rose-600',     bg: 'bg-rose-100',     helper: 'Photos, vidéo, visite virtuelle' },
  { id: 'documents',    label: 'Documents internes',    icon: 'bi-folder-fill',       iconColor: 'text-slate-600',   bg: 'bg-slate-100',   helper: 'Documents privés de l\'agence (mandats, diagnostics, pièces)', divider: true },
  { id: 'confidentiel', label: 'Confidentiel',          icon: 'bi-shield-lock',       iconColor: 'text-amber-600',    bg: 'bg-amber-100',    helper: 'Adresse exacte, identité, conditions', confidential: true },
];

/**
 * Détecte si la taxonomie correspond à un actif INCORPOREL "licence".
 * Une licence d'exploitation peut avoir n'importe quelle opération (cession, location, location_droit_entree),
 * la détection doit donc se baser sur l'objet/profil ET pas uniquement sur l'opération.
 * @param {Object|string} taxonomy
 * @returns {boolean}
 */
export function isLicenceAsset(taxonomy) {
  if (!taxonomy) return false;
  const t = typeof taxonomy === 'string' ? { operation: taxonomy } : taxonomy;
  const lc = (v) => (v || '').toString().toLowerCase();
  return (
    lc(t.operation) === 'licence' ||
    lc(t.objet) === 'licence' ||
    lc(t.objet) === 'licence_exploitation' ||
    lc(t.profil) === 'licence'
  );
}

/**
 * Détecte si la taxonomie correspond à une cession de PARTS SOCIALES (actif incorporel).
 */
export function isPartsSocialesAsset(taxonomy) {
  if (!taxonomy) return false;
  const t = typeof taxonomy === 'string' ? { operation: taxonomy } : taxonomy;
  const lc = (v) => (v || '').toString().toLowerCase();
  return (
    lc(t.operation) === 'parts_sociales' ||
    lc(t.objet) === 'parts_sociales'
  );
}

/**
 * Retourne les onglets visibles pour une opération/taxonomie donnée.
 * Compatible string (ancienne signature) et objet (nouvelle signature avec profil/objet).
 * @param {Object|string} taxonomyOrOperation
 */
export function getVisibleTabs(taxonomyOrOperation) {
  const isStr = typeof taxonomyOrOperation === 'string';
  const operation = isStr ? taxonomyOrOperation : taxonomyOrOperation?.operation;
  const ctx = isStr ? { operation } : (taxonomyOrOperation || {});
  const licence = isLicenceAsset(ctx);
  const parts = isPartsSocialesAsset(ctx);

  return TABS.filter(t => {
    // Diagnostics : pas applicable pour les actifs incorporels (licence, parts sociales)
    if (t.id === 'diagnostics' && (licence || parts)) return false;
    // Détails : masqué pour les LICENCES seules (actif incorporel, aucun local à décrire).
    // iter132 — les PARTS SOCIALES gardent l'onglet : elles portent un fonds
    // (bloc « Le fonds » + « Les chiffres » de L'exploitation).
    if (t.id === 'composition' && licence) return false;
    // iter106 : Onglet exploitation supprimé (fusionné dans Détails/composition).
    if (t.opOnly && !t.opOnly.includes(operation)) return false;
    if (t.excludeOp && t.excludeOp.includes(operation)) return false;
    return true;
  });
}

/**
 * Retourne la liste des erreurs (champs obligatoires manquants) pour un onglet donné.
 * Utilisé pour bloquer la navigation tant que l'onglet courant n'est pas valide.
 * @returns {Array<{field: string, label: string}>}
 */
export function getTabRequiredErrors(tabId, formData, taxonomy) {
  const errors = [];
  const tax = taxonomy || {};
  const c = formData?.composition || {};

  switch (tabId) {
    case 'general': {
      if (!tax.operation || !tax.objet || !tax.profil) {
        errors.push({ field: 'taxonomy', label: 'Type de bien (opération · objet · activité)' });
      }
      // Le secteur géographique est OPTIONNEL :
      // - Le wizard ne le demande pas (il sera auto-déduit depuis la ville à l'affichage public)
      // - Pour les licences/parts sociales, la notion de "secteur de marché" n'a pas de sens
      // → on ne bloque plus l'édition là-dessus. Si on veut le rappeler, ce sera un warning de complétude.
      break;
    }
    case 'composition': {
      // Composition exclue pour licence / parts sociales (cf. excludeOp)
      if (isLicenceAsset(tax) || isPartsSocialesAsset(tax)) break;
      const surf = parseFloat(c.superficie_totale) || 0;
      if (surf <= 0) {
        errors.push({ field: 'superficie_totale', label: 'Surface totale (m²)' });
      }
      break;
    }
    default:
      break;
  }
  return errors;
}

/**
 * Calcule le niveau de complétude d'un onglet.
 * @returns 'full' | 'partial' | 'empty'
 */
export function getTabCompletion(tabId, formData, taxonomy) {
  const safe = (fn) => { try { return fn(); } catch (e) { return 0; } };

  switch (tabId) {
    case 'general': {
      const tax = taxonomy || {};
      const bg = formData?.bien_general || {};
      const hasTax = !!(tax.operation && tax.objet && tax.profil);
      const hasTitle = !!bg.titre_annonce;
      const hasCity = !!(formData?.public_location?.city_label || bg.ville_generale);
      const n = (hasTax ? 1 : 0) + (hasTitle ? 1 : 0) + (hasCity ? 1 : 0);
      return n === 3 ? 'full' : n > 0 ? 'partial' : 'empty';
    }
    case 'composition': {
      // iter106 — Fusion Composition + Exploitation : la pastille couvre les 2 blocs.
      const c = formData?.composition || {};
      const bg = formData?.bien_general || {};
      const of = formData?.ouverture_fermeture || {};
      const surf = parseFloat(c.superficie_totale) || 0;
      const extras = [c.extraction, c.climatisation, c.pmr, c.gaz, c.triphase, c.chambre_froide].filter(Boolean).length;
      const exploitFilled = [bg.etat_general, bg.anciennete_activite, of.horaires_ouverture_fermeture].filter(Boolean).length;
      const physical = surf > 0 && extras >= 2;
      if (physical && exploitFilled >= 2) return 'full';
      if (surf > 0 || extras > 0 || exploitFilled > 0) return 'partial';
      return 'empty';
    }
    case 'descriptif': {
      const bg = formData?.bien_general || {};
      const hasCourte = !!bg.description_courte;
      const descs = formData?.descriptif?.descriptions;
      const hasDetail = descs && Object.keys(descs).some(type =>
        Object.keys(descs[type] || {}).some(lang => descs[type][lang]?.content)
      );
      if (hasCourte && hasDetail) return 'full';
      if (hasCourte || hasDetail) return 'partial';
      return 'empty';
    }
    case 'exploitation': {
      // iter106 — Onglet fusionné dans « Détails » (composition). Cas gardé pour rétrocompat éventuelle.
      const bg = formData?.bien_general || {};
      const of = formData?.ouverture_fermeture || {};
      const filled = [bg.etat_general, bg.anciennete_activite, of.horaires_ouverture_fermeture].filter(Boolean).length;
      if (filled >= 3) return 'full';
      if (filled > 0) return 'partial';
      return 'empty';
    }
    case 'financieres': {
      const v = formData?.informations_vente || {};
      const b = formData?.informations_bail || {};
      const hasPrice = !!(v.net_vendeur || v.prix_cession || b.loyer_mensuel || b.redevance_mensuelle);
      const hasExtras = !!(b.charges_mensuelles || b.depot_garantie || v.capital_social || formData?.informations_confidentielles?.ca_n1);
      if (hasPrice && hasExtras) return 'full';
      if (hasPrice) return 'partial';
      return 'empty';
    }
    case 'diagnostics': {
      const bg = formData?.bien_general || {};
      const hasDpe = !!bg.dpe;
      const hasGes = !!bg.ges;
      if (hasDpe && hasGes) return 'full';
      if (hasDpe || hasGes) return 'partial';
      return 'empty';
    }
    case 'mandat': {
      const bg = formData?.bien_general || {};
      const numero = formData?.numero_mandat || bg.numero_mandat || formData?.mandat?.numero_mandat;
      const type = bg.type_mandat || formData?.type_mandat || formData?.mandat?.type;
      const dateSignature = bg.date_mandat || formData?.date_mandat || formData?.mandat?.date_signature;
      const dateFin = bg.date_fin_mandat || formData?.date_fin_mandat || formData?.mandat?.date_fin;
      const nego = formData?.negociateur_id || formData?.negociateur?.negociateur;
      const n = (numero ? 1 : 0) + (type ? 1 : 0) + (dateSignature ? 1 : 0) + (nego ? 1 : 0) + (dateFin ? 1 : 0);
      if (n >= 4) return 'full';
      if (n > 0) return 'partial';
      return 'empty';
    }
    case 'medias': {
      const photos = formData?.medias?.photos || [];
      const visualTheme = formData?.medias?.visuel_thematique;
      if (photos.length >= 3 || visualTheme) return 'full';
      if (photos.length > 0) return 'partial';
      return 'empty';
    }
    case 'documents': {
      const docs = formData?.medias?.documents || [];
      return docs.length >= 2 ? 'full' : docs.length > 0 ? 'partial' : 'empty';
    }
    case 'confidentiel': {
      const hasAddress = !!formData?.confidential_location?.street;
      const prop = formData?.informations_confidentielles?.proprietaire;
      const hasProp = !!(prop?.nom || prop?.societe);
      if (hasAddress && hasProp) return 'full';
      if (hasAddress || hasProp) return 'partial';
      return 'empty';
    }
    default:
      return 'empty';
  }
}

/**
 * Liste des champs critiques manquants pour publication.
 * Retourne une liste d'objets { label, tabId }.
 */
export function getMissingCriticalFields(formData, taxonomy) {
  const out = [];
  const bg = formData?.bien_general || {};
  const tax = taxonomy || {};

  // Surface totale (composition) — pas applicable aux actifs incorporels
  if (!isLicenceAsset(tax) && !isPartsSocialesAsset(tax)) {
    const surf = parseFloat(formData?.composition?.superficie_totale) || 0;
    if (surf <= 0) out.push({ label: 'Surface totale (m²)', tabId: 'composition' });
  }

  const numero = formData?.numero_mandat || bg.numero_mandat || formData?.mandat?.numero_mandat;
  if (!numero) out.push({ label: 'Numéro de mandat', tabId: 'mandat' });

  const dateSignature = bg.date_mandat || formData?.date_mandat || formData?.mandat?.date_signature;
  if (!dateSignature) out.push({ label: 'Date de signature du mandat', tabId: 'mandat' });

  const photos = formData?.medias?.photos || [];
  const visualTheme = formData?.medias?.visuel_thematique;
  if (photos.length === 0 && !visualTheme) out.push({ label: 'Photo principale', tabId: 'medias' });

  const prop = formData?.informations_confidentielles?.proprietaire;
  if (!prop?.nom && !prop?.societe) out.push({ label: 'Propriétaire', tabId: 'confidentiel' });

  const v = formData?.informations_vente || {};
  const b = formData?.informations_bail || {};
  const hasPrice = !!(v.net_vendeur || v.prix_cession || b.loyer_mensuel || b.redevance_mensuelle);
  if (!hasPrice) out.push({ label: 'Prix / loyer', tabId: 'financieres' });

  return out;
}

/**
 * Status d'un bien → chip class + dot class + label.
 */
export function getStatusInfo(status) {
  const s = (status || 'draft').toLowerCase();
  const MAP = {
    active:   { label: 'ACTIF',          chip: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500',  raw: 'active' },
    actif:    { label: 'ACTIF',          chip: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500',  raw: 'active' },
    draft:    { label: 'BROUILLON',      chip: 'bg-amber-100 text-amber-700',     dot: 'bg-amber-500',    raw: 'draft' },
    brouillon:{ label: 'BROUILLON',      chip: 'bg-amber-100 text-amber-700',     dot: 'bg-amber-500',    raw: 'draft' },
    compromis:{ label: 'SOUS COMPROMIS', chip: 'bg-blue-100 text-blue-700',       dot: 'bg-blue-500',     raw: 'compromis' },
    sous_compromis:{ label: 'SOUS COMPROMIS', chip: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500',     raw: 'compromis' },
    vendu:    { label: 'VENDU / LOUÉ',   chip: 'bg-slate-900 text-white',         dot: 'bg-slate-900',    raw: 'vendu' },
    loue:     { label: 'VENDU / LOUÉ',   chip: 'bg-slate-900 text-white',         dot: 'bg-slate-900',    raw: 'vendu' },
    archived: { label: 'ARCHIVÉ',        chip: 'bg-slate-100 text-slate-600',     dot: 'bg-slate-400',    raw: 'archived' },
    archive:  { label: 'ARCHIVÉ',        chip: 'bg-slate-100 text-slate-600',     dot: 'bg-slate-400',    raw: 'archived' },
  };
  return MAP[s] || MAP.draft;
}

// Export utile pour tests unitaires hypothétiques
export const __internals = { getByPath };
